// Two-tap distance measurement tool.
//
// Sprint 1 scope (panorama-only rooms): the user taps two points; we cast a
// ray from the camera through each tap, project onto a fixed-radius sphere,
// and report the angular separation × per-room `metresPerRadian` calibration.
//
// Sprint 2 follow-up: when a room becomes a Gaussian splat or glTF, the depth
// buffer gives real distances and calibration becomes auto.

import * as pc from 'playcanvas';
import { Emitter } from '../app/emitter';

const MEASURE_LAYER_NAME = 'measure';
const MARKER_RADIUS = 0.32;
const MEASURE_SPHERE_RADIUS = 9;
const LINE_COLOR = new pc.Color(0.2, 1, 0.92);

export type MeasureMode = 'off' | 'on';

export interface MeasurementUpdate {
  isActive: boolean;
  pointCount: 0 | 1 | 2;
  distanceM?: number;
  metresPerRadian?: number;
}

interface MeasurePoint {
  entity: pc.Entity;
  worldPos: pc.Vec3;
  unit: pc.Vec3; // unit-vector direction from camera position at placement time
}

interface ActiveMeasurement {
  pointA: MeasurePoint;
  pointB: MeasurePoint | null;
  label: HTMLElement | null;
}

export class MeasureTool {
  private readonly app: pc.Application;
  private readonly camera: pc.Entity;
  private readonly canvas: HTMLCanvasElement;
  private readonly container: HTMLElement;
  private readonly emitter = new Emitter<MeasurementUpdate>();
  private readonly screenScratch = new pc.Vec3();
  private readonly layer: pc.Layer;

  private mode: MeasureMode = 'off';
  private metresPerRadian = 2.0;
  private current: ActiveMeasurement | null = null;
  private markerMaterial: pc.StandardMaterial | null = null;

  constructor(
    app: pc.Application,
    camera: pc.Entity,
    canvas: HTMLCanvasElement,
    container: HTMLElement,
  ) {
    this.app = app;
    this.camera = camera;
    this.canvas = canvas;
    this.container = container;

    // Marker entities live on a dedicated layer so they don't show up in the
    // hotspot picker buffer (and don't get accidentally clicked).
    const existing = app.scene.layers.getLayerByName(MEASURE_LAYER_NAME);
    if (existing) {
      this.layer = existing;
    } else {
      this.layer = new pc.Layer({ name: MEASURE_LAYER_NAME });
      app.scene.layers.push(this.layer);
    }
    if (camera.camera) {
      const camLayers = camera.camera.layers ?? [];
      if (!camLayers.includes(this.layer.id)) {
        camera.camera.layers = [...camLayers, this.layer.id];
      }
    }

    this.app.on('update', this.tick);
  }

  setMetresPerRadian(value: number): void {
    if (!Number.isFinite(value) || value <= 0) return;
    this.metresPerRadian = value;
    this.refreshLabel();
  }

  setMode(mode: MeasureMode): void {
    if (this.mode === mode) return;
    this.mode = mode;
    this.canvas.style.cursor = mode === 'on' ? 'crosshair' : '';
    if (mode === 'off') this.clear();
    this.emitFromState();
  }

  toggleMode(): void {
    this.setMode(this.mode === 'on' ? 'off' : 'on');
  }

  isActive(): boolean {
    return this.mode === 'on';
  }

  on(listener: (e: MeasurementUpdate) => void): () => void {
    return this.emitter.on(listener);
  }

  /** Called from App's canvas tap handler whenever measure mode is on. */
  addPointAtClient(clientX: number, clientY: number): void {
    if (this.mode !== 'on') return;
    const cam = this.camera.camera;
    if (!cam) return;

    // Cast a world-space ray from the click and place the marker on a
    // fixed-radius sphere around the camera.
    const rect = this.canvas.getBoundingClientRect();
    const x = clientX - rect.left;
    const y = clientY - rect.top;
    const near = new pc.Vec3();
    const far = new pc.Vec3();
    cam.screenToWorld(x, y, cam.nearClip, near);
    cam.screenToWorld(x, y, cam.farClip, far);
    const dir = new pc.Vec3().sub2(far, near).normalize();

    const camPos = this.camera.getPosition();
    const worldPos = new pc.Vec3()
      .copy(camPos)
      .add(new pc.Vec3().copy(dir).mulScalar(MEASURE_SPHERE_RADIUS));
    const unit = new pc.Vec3().copy(dir);

    const entity = this.spawnMarker(worldPos);
    const point: MeasurePoint = { entity, worldPos, unit };

    if (!this.current) {
      this.current = { pointA: point, pointB: null, label: null };
    } else if (!this.current.pointB) {
      this.current.pointB = point;
      this.current.label = this.spawnLabel();
      this.refreshLabel();
    } else {
      // Third tap → start a new measurement.
      this.clear();
      this.current = { pointA: point, pointB: null, label: null };
    }
    this.emitFromState();
  }

  clear(): void {
    if (!this.current) return;
    this.current.pointA.entity.destroy();
    this.current.pointB?.entity.destroy();
    this.current.label?.remove();
    this.current = null;
    this.emitFromState();
  }

  destroy(): void {
    this.clear();
    this.markerMaterial?.destroy();
    this.markerMaterial = null;
    this.app.off('update', this.tick);
  }

  // ── Internals ─────────────────────────────────────────────────

  private spawnMarker(pos: pc.Vec3): pc.Entity {
    const entity = new pc.Entity('measure-point');
    entity.addComponent('render', { type: 'sphere', layers: [this.layer.id] });
    entity.setLocalScale(MARKER_RADIUS, MARKER_RADIUS, MARKER_RADIUS);
    entity.setPosition(pos);

    if (!this.markerMaterial) {
      const mat = new pc.StandardMaterial();
      mat.useLighting = false;
      mat.useFog = false;
      mat.useTonemap = false;
      mat.useSkybox = false;
      mat.diffuse = new pc.Color(0, 0, 0);
      mat.emissive = LINE_COLOR;
      mat.opacity = 0.92;
      mat.blendType = pc.BLEND_NORMAL;
      mat.depthWrite = false;
      mat.update();
      this.markerMaterial = mat;
    }
    const meshInstances = entity.render?.meshInstances ?? [];
    for (const mi of meshInstances) mi.material = this.markerMaterial;

    this.app.root.addChild(entity);
    return entity;
  }

  private spawnLabel(): HTMLElement {
    const label = document.createElement('div');
    label.className = 'measure-label';
    this.container.appendChild(label);
    return label;
  }

  private refreshLabel(): void {
    if (!this.current?.label) return;
    const distM = this.computeDistance();
    this.current.label.textContent = `${distM.toFixed(2)} m`;
  }

  private computeDistance(): number {
    if (!this.current?.pointB) return 0;
    const a = this.current.pointA.unit;
    const b = this.current.pointB.unit;
    const dot = Math.max(-1, Math.min(1, a.dot(b)));
    const angleRad = Math.acos(dot);
    return angleRad * this.metresPerRadian;
  }

  private emitFromState(): void {
    const isActive = this.mode === 'on';
    const count: 0 | 1 | 2 = !this.current ? 0 : this.current.pointB ? 2 : 1;
    const update: MeasurementUpdate = { isActive, pointCount: count, metresPerRadian: this.metresPerRadian };
    if (count === 2) update.distanceM = this.computeDistance();
    this.emitter.emit(update);
  }

  private readonly tick = (_dt: number): void => {
    if (!this.current) return;
    const cam = this.camera.camera;
    if (!cam) return;

    if (this.current.pointB) {
      // Immediate-mode line A → B (re-issued every frame).
      this.app.drawLine(this.current.pointA.worldPos, this.current.pointB.worldPos, LINE_COLOR, false);

      if (this.current.label) {
        const mid = new pc.Vec3()
          .copy(this.current.pointA.worldPos)
          .add(this.current.pointB.worldPos)
          .mulScalar(0.5);
        cam.worldToScreen(mid, this.screenScratch);
        const inFront = this.screenScratch.z > 0;
        if (!inFront) {
          this.current.label.style.display = 'none';
        } else {
          this.current.label.style.display = '';
          // PC 2.19's worldToScreen returns CSS pixels — see the matching
          // comment in hotspotManager.tick. Earlier /ratio division was a
          // no-op at DPR=1 but drifted the label toward (0,0) on scaled displays.
          this.current.label.style.left = `${this.screenScratch.x}px`;
          this.current.label.style.top = `${this.screenScratch.y}px`;
        }
      }
    }
  };
}
